import { useCallback, useRef } from 'react';

import {
	BFF_REQUEST_STATUS_KEY,
	MAX_ATTEMPTS_DEFAULT,
	RequestStatus,
	RETRY_INTERVAL_DEFAULT,
} from '../../constants';
import { fetchJSON } from '../utils/request';

import { useCache } from './useCache';
import { useProductLinks } from './useProductLinks';
import { useStatus } from './useStatus';
import { useURLBuilder } from './useURLBuilder';

type useBFFProps = {
	cloudId?: string;
	forceStatic?: boolean;
	orgId?: string;
	product?: string;
	request?: {
		attemptInterval?: number;
		maxAttempts?: number;
		shouldRetryOnException?: boolean;
	};
	userId?: string;
};

async function wait(ms: number) {
	return new Promise((resolve) => {
		setTimeout(resolve, ms);
	});
}

export function useBFFDataProvider({
	request,
	cloudId,
	orgId,
	product,
	userId,
	forceStatic,
}: useBFFProps) {
	const { items: cachedItems, addItemsToCache, clearCache, cacheKey, updateCacheKey } = useCache();
	const { setStatus, status } = useStatus();
	const { productLinks } = useProductLinks({ cloudId, orgId, forceStatic });
	const { buildURL } = useURLBuilder({ cloudId, userId, product });

	const currentRequest = useRef<Promise<any> | null>(null);

	const fetchData = useCallback(
		async <T>(
			url: string,
			data?: any,
			attemptCount = 1,
			successCheck?: (result: any) => boolean,
		): Promise<any> => {
			if (url === '') {
				throw new Error('URL is empty');
			}

			setStatus(RequestStatus.LOADING, BFF_REQUEST_STATUS_KEY);

			try {
				const response = await fetchJSON<T>(url, {});
				switch (response.status) {
					case RequestStatus.ERROR:
						throw response.error;
					case RequestStatus.COMPLETE:
						if (!successCheck || successCheck(response.data)) {
							setStatus(RequestStatus.COMPLETE, BFF_REQUEST_STATUS_KEY);
							currentRequest.current = null;

							return response.data;
						} else {
							throw new Error('BFF success check failed');
						}
				}
			} catch (e) {
				const maxAttempts = request?.maxAttempts ?? MAX_ATTEMPTS_DEFAULT;

				if (request?.shouldRetryOnException && attemptCount < maxAttempts) {
					// Introduce longer wait times for each retry attempt
					await wait((request?.attemptInterval ?? RETRY_INTERVAL_DEFAULT) * attemptCount);
					return await fetchData(url, data, attemptCount + 1, successCheck);
				} else {
					currentRequest.current = null;
					setStatus(RequestStatus.ERROR);
					throw new Error('Max attempts reached');
				}
			}
		},
		[request?.attemptInterval, request?.maxAttempts, request?.shouldRetryOnException, setStatus],
	);

	/**
	 * Helper function to build a URL and fetch data.
	 */
	const buildURLAndFetchData = useCallback(async <T>(): Promise<void> => {
		// Wait for the current request to end before firing another
		if (currentRequest.current) {
			return currentRequest.current;
		}

		const builtUrl = buildURL({});

		if (builtUrl !== '') {
			currentRequest.current = fetchData<T>(builtUrl, undefined, undefined, (result) => {
				return result.sites && result.sites.length > 0;
			});

			const fetchedData = await currentRequest.current;

			if (fetchedData.sites && fetchedData.sites.length) {
				addItemsToCache(fetchedData.sites);
			}
		}
	}, [addItemsToCache, buildURL, fetchData]);

	return {
		status,
		cachedItems,
		cacheKey,
		updateCacheKey,
		addItemsToCache,
		clearCache,
		buildURL,
		fetchData,
		buildURLAndFetchData,
		productLinks,
	};
}
